import React, {useState, useEffect} from 'react';
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Input,
} from '@chakra-ui/react';
import {useFieldArray, useForm} from 'react-hook-form';
import {isAdmin} from '../../../helpers/localStorage';
import {post, get} from '../../../service/ApiService';
import ErrorHelper from '../../../service/ErrorHelper';
import {useClient} from '../../../views/Maintenance/ClientMaintenance';
import {CustomInput, CustomSelect} from '../../General/FormInputs';
import {useQueryClient, useQuery} from 'react-query';
import {FaPlusSquare, FaTrash} from 'react-icons/fa';
import {EmailContact} from '../../../constants/client/types';
import {Client as ClientUrls} from '../../../service/Urls';

interface EmailForm {
  emails: EmailContact[];
}

export const EMAIL_CATEGORIES = [
  /*{value: 'Support', label: 'Support'},
  {value: 'Billing', label: 'Billing'},
  {value: 'General', label: 'General'},*/
  {value: 'Positive Survey', label: 'Positive Survey', report: 'survey2_email.rdlx', subject: 'Positives: Positive Survey'},
  {value: 'Needs Attention', label: 'Needs Attention', report: 'survey2_email.rdlx', subject: 'Negative & Needs Attention: Survey for Your Attention'},
  {value: 'Negative Survey', label: 'Negative Survey', report: 'survey2_email.rdlx', subject: 'Negative & Needs Attention: Survey for Your Attention'},
  {value: 'Chinese Survey', label: 'Chinese Survey', report: 'survey2_email.rdlx'},
];

export default function Emails() {
  const {client, selectedClientId} = useClient();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<any>(null);
  const [isSalesMan, setIsSalesMan] = useState(true);
  const [emailContacts, setEmailContacts] = useState<EmailContact[]>([]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: {errors},
  } = useForm<EmailForm>();

  const {fields, append, remove} = useFieldArray({
    name: 'emails',
    control,
  });

  // Fetch email contacts when client is selected
  const {data: emailData, isLoading: isLoadingEmails, refetch} = useQuery(
    ['clientEmails', selectedClientId],
    () => get(`${ClientUrls.EMAIL_LIST}?page=1&pageSize=100&clId=${selectedClientId}`),
    {
      enabled: !!selectedClientId,
      onSuccess: (data: any) => {
        if (data?.isSuccess && data?.data) {
          setEmailContacts(data.data);
          reset({
            emails: data.data.map((email: EmailContact) => ({
              id: email.id,
              clId: email.clId,
              category: email.category,
              name: email.name,
              email: email.email,
              createdAt: email.createdAt,
              clientName: email.clientName,
            })),
          });
        }
      },
      onError: () => {
        setToast({error: 'Error loading email contacts'});
      },
    }
  ) as any;

  useEffect(() => {
    if (isAdmin()) {
      setIsSalesMan(false);
    }
  }, []);

  const addEmail = () => {
    append({
      clId: selectedClientId ?? undefined,
      category: '',
      name: '',
      email: '',
    });
  };

  const onSubmit = async (data: EmailForm) => {
    if (isSalesMan) {
      setToast({response: {error: '403'}, errorMessage: 'Invalid Permission'});
      return;
    }

    const payload = {
      clId: selectedClientId ?? null,
      clientEmails: (data?.emails ?? []).map(e => ({
        category: e.category,
        name: e.name,
        email: e.email,
      })),
    };

    const response: any = await post(ClientUrls.EMAIL_BULK_REPLACE, payload);

    if (response?.isSuccess) {
      queryClient.invalidateQueries(['clientEmails', selectedClientId]);
      setToast({response: response, successMessage: 'Successfully updated email contacts'});
    } else {
      setToast({response: response, errorMessage: response?.error || 'Request Was Unsuccessful'});
    }
  };

  if (isLoadingEmails) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Text>Loading email contacts...</Text>
      </Flex>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gap={3} mr={5}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">Email Contacts</Heading>
          <Flex gap={2}>
            {!isSalesMan && (
              <Button
                type="button"
                colorScheme="secondary"
                leftIcon={<FaPlusSquare />}
                onClick={addEmail}
                size="sm"
              >
                Add
              </Button>
            )}
            <Button type="submit" colorScheme="secondary" size="sm" disabled={isSalesMan}>
              Save
            </Button>
          </Flex>
        </Flex>

        <TableContainer>
          <Table
            width={{base: '100%', '2xl': '85%'}}
            border="solid 2px #0c5f78"
            variant="striped"
            colorScheme="mint"
            size={'md'}
          >
            <TableCaption>Email Contacts for {client?.clCname || 'Client'}</TableCaption>
            <Thead>
              <Tr border="solid 2px #0c5f78">
                <Th fontWeight="bold" textTransform="uppercase">Category</Th>
                <Th fontWeight="bold" textTransform="uppercase">Name</Th>
                <Th fontWeight="bold" textTransform="uppercase">Email</Th>
                {!isSalesMan && <Th fontWeight="bold" textTransform="uppercase">Actions</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {fields.map((field, index) => {
                const categoryId = `emails.${index}.category` as const;
                const nameId = `emails.${index}.name` as const;
                const emailId = `emails.${index}.email` as const;

                const emailErrors = errors?.emails?.[index] ?? {};
                
                return (
                  <Tr key={field.id} bg={index % 2 === 0 ? 'green.50' : 'white'}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <FormLabel fontSize="sm" mb={0}>Category</FormLabel>
                        <Select
                          {...register(categoryId, { required: true })}
                          placeholder="Select Category"
                          disabled={isSalesMan}
                          size="sm"
                        >
                          {EMAIL_CATEGORIES.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </Select>
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <FormLabel fontSize="sm" mb={0}>Name</FormLabel>
                        <Input
                          {...register(nameId, { required: true })}
                          placeholder="Enter name"
                          readOnly={isSalesMan}
                          size="sm"
                          px={2}
                          py={1}
                        />
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <FormLabel fontSize="sm" mb={0}>Email</FormLabel>
                        <Input
                          {...register(emailId, { required: true })}
                          type="email"
                          placeholder="Enter email"
                          readOnly={isSalesMan}
                          size="sm"
                          px={2}
                          py={1}
                        />
                      </VStack>
                    </Td>
                    {!isSalesMan && (
                      <Td>
                        <IconButton
                          colorScheme="red"
                          size="sm"
                          icon={<FaTrash />}
                          onClick={() => remove(index)}
                          aria-label="Delete Email"
                          title="Delete Email"
                        />
                      </Td>
                    )}
                  </Tr>
                );
              })}
              {fields.length === 0 && (
                <Tr>
                  <Td colSpan={isSalesMan ? 3 : 4} textAlign="center" py={8}>
                    <Text color="gray.500" fontSize="sm">
                      No email contacts configured. Click "Add" to create one.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Flex>
      <ErrorHelper toastResponse={toast} />
    </form>
  );
}
